import { create, Jss } from 'jss';
import camelCase from 'jss-plugin-camel-case';
import { getCspNonce } from './csp';

/**
 * Setup a MutationObserver to add nonce to all dynamically created style tags
 * This catches JSS and other libraries that create style elements
 */
function setupStyleNonceObserver() {
  const nonce = getCspNonce();
  if (!nonce) return;

  // Observe the document for new style elements
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        // Check if the added node is a style element
        if (node instanceof HTMLStyleElement && !node.hasAttribute('nonce')) {
          node.setAttribute('nonce', nonce);
        }
        // Also check children if it's an element
        if (node instanceof HTMLElement) {
          const styleElements = node.querySelectorAll('style:not([nonce])');
          for (const style of styleElements) {
            if (style instanceof HTMLStyleElement) {
              style.setAttribute('nonce', nonce);
            }
          }
        }
      }
    }
  });

  // Start observing the document head and body
  observer.observe(document.head, { childList: true, subtree: true });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Also add nonce to any existing style elements without it
  for (const style of document.querySelectorAll('style:not([nonce])')) {
    if (style instanceof HTMLStyleElement) {
      style.setAttribute('nonce', nonce);
    }
  }
}

/**
 * Create JSS instance with CSP nonce support and camelCase conversion
 * This configures JSS to:
 * - Add nonce attributes to all dynamically created style tags
 * - Convert camelCase properties to kebab-case CSS (e.g., flexDirection -> flex-direction)
 */
export function createJssWithNonce(): Jss {
  // Start the mutation observer to catch all style elements
  setupStyleNonceObserver();

  // Create JSS instance with camelCase plugin
  return create({
    plugins: [camelCase()],
  });
}

export { JssProvider } from 'react-jss';
